/**
 * Typographic quotes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const typographicQuotesPack: ToilePack = {
    slug: 'typographic-quotes',
    category: 'wall-art',
    name: 'Typographic quotes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'breathe', label: 'Breathe', subject: 'the single word "BREATHE" as the whole picture, set on one line and filling the width of the sheet'},
        {key: 'morning', label: 'Good morning', subject: 'the two words "GOOD MORNING" as the whole picture, stacked on two lines and centered'},
        {key: 'wild', label: 'Stay wild', subject: 'the two words "STAY WILD" as the whole picture, set on one line with a thin horizontal rule beneath'},
    ],
    styles: [
        {key: 'serif', label: 'Serif', style: 'oversized high-contrast serif lettering, thick and thin strokes, flat solid type with no outlines and no ornament', palette: 'black type on a warm cream ground', framing: 'the type centered with a generous even margin on a plain cream background'},
        {key: 'sans', label: 'Sans', style: 'heavy geometric sans-serif lettering set tight, three weights in one block, flat solid fills with hard edges', palette: 'off-white type on deep forest green', framing: 'the type block centered and filling most of the sheet against a solid background'},
        {key: 'letterpress', label: 'Letterpress', style: 'wide letterspaced slab capitals with a thin rule above and below, a subtle letterpress impression and visible paper grain', palette: 'warm ink brown on ivory paper', framing: 'the line centered high on an ivory paper background with a deep margin below'},
    ],
    directions: ['a longer quote instead', 'for a kitchen wall', 'as a birthday card'],
    seo: {
        title: 'Typography quote prints: breathe, morning, wild',
        metaDescription: 'Nine typographic quote prints — Breathe, Good morning, Stay wild — set in serif, sans or letterpress type, spelled exactly and centered.',
        h1: 'Typography quote prints: breathe, good morning, stay wild',
        tagline: 'Nine prints, three lines, three type treatments each — spelled exactly, centered, readable from the doorway.',
        intro: 'A typography print is the word doing the whole job: BREATHE, GOOD MORNING, STAY WILD, set oversized and centered with nothing else on the page. These nine cross those three lines with an oversized serif, a stacked sans in three weights, and a letterpress-textured caps treatment — the print above the desk that reads from across the room.',
        useCases: [
            'the print above a desk',
            'a bedroom or entryway wall',
            'a gift with a chosen line',
            'a set paired above a headboard',
        ],
        faq: [
            {q: 'Can I use my own quote instead?', a: 'Yes — type a different line in the direction field and it is spelled exactly, set in the same three type treatments.'},
            {q: 'Is the text guaranteed to be spelled correctly?', a: 'Yes — the line is composed exactly as typed, centered on the page with no other artwork.'},
            {q: 'What size do these print at?', a: 'Each print is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
        ],
    },
}
